import {
    AccountWithExtraInfo,
    GroupRtbs,
    Model,
    ModelRtbs,
    ModelWithExtraInfo,
} from '@orbis-systems/orbis-api-middleware';
import { QueryClient } from '@tanstack/react-query';

import { MODEL_PREFERENCES_DATA_PREFIX } from '@/features/models/models-constants';
import { combineAccountsWithRtbs, formatPortfolio, getPortfolioSecurityTotalValue } from '../branch/adapters';
import { combinePositions } from '../branch/combinePositions';
import { branchQueries } from '../branch/queries';
import { modelQueries } from './queries';

export const buildModelPortfolioWithBreakdownFromCache =
    ({ currency, modelId }: { currency: string; modelId: number }) =>
    ({ client }: { client: QueryClient }) => {
        const branchPortfolio = client.getQueryData(
            branchQueries.branchPortfolio({ currency, combined: false }).queryKey,
        )?.data;
        const branchRtbs = client.getQueryData(branchQueries.branchRtbs().queryKey)?.data;
        const modelMembers = client.getQueryData(
            modelQueries.memberAccounts({ modelId: modelId.toString() }).queryKey,
        )?.data;
        if (branchPortfolio && branchRtbs && modelMembers) {
            client.removeQueries({
                type: 'inactive',
                queryKey: [...modelQueries.all().queryKey, 'modelPortfolioWithBreakdown', modelId],
            });
            const { entries: branchPositions, ...restPortfolio } = branchPortfolio;
            const modelAccountIds = modelMembers.map((item) => item.account.accountId);
            const modelAccounts = modelMembers.map((item) => item.account);
            const modelRtbs = branchRtbs.filter((item) => modelAccountIds.includes(item.accountId));
            const modelPortfolioUncombined = branchPortfolio.entries.filter((item) =>
                modelAccountIds.includes(item.accountId),
            );
            const modelAccountsWithRtbs = combineAccountsWithRtbs(modelAccounts, modelRtbs);
            const modelPortfolioCombined = combinePositions(modelPortfolioUncombined);
            const modelSecurityTotalValue = getPortfolioSecurityTotalValue(modelPortfolioCombined);
            const modelPortfolioWithCombinedPositions = {
                ...restPortfolio,
                entries: modelPortfolioCombined,
                currency,
                modelSecurityTotalValue,
            };
            const modelPortfolio = formatPortfolio(
                modelPortfolioWithCombinedPositions,
                modelAccountsWithRtbs,
                [],
                modelSecurityTotalValue,
            );
            const modelAccountMap = modelAccountsWithRtbs.reduce<Record<number, AccountWithExtraInfo>>(
                (acc, account) => {
                    acc[account.accountId] = account;
                    return acc;
                },
                {},
            );
            return { modelPortfolio, modelAccountMap };
        } else throw new Error('modelPortfolioWithBreakdown: expected source queries populated');
    };

export interface ModelWithBaseCurrency extends Omit<ModelWithExtraInfo, 'accounts'> {
    accounts: AccountWithExtraInfo[];
    accountsCount: number;
    baseCurrency: string;
    defaultRebalanceThreshold: number;
}

type FormatModelWithRtbsAndBaseCurrencyParams = {
    model: Model;
    modelRtbs?: ModelRtbs;
    baseCurrency: string;
    fxRate: number;
    defaultRebalanceThreshold: number;
    accountRtbs?: GroupRtbs[];
};

export const formatModelWithRtbsAndBaseCurrency = ({
    model,
    modelRtbs,
    baseCurrency,
    fxRate,
    defaultRebalanceThreshold,
    accountRtbs = [],
}: FormatModelWithRtbsAndBaseCurrencyParams): ModelWithBaseCurrency => {
    const {
        balance = 0,
        cashOnAccount = 0,
        markToMarket = 0,
        totalDividendAmount = 0,
        totalDividendYield = 0,
        sodCashBalance = 0,
        sodMarginBalance = 0,
        sodShortBalance = 0,
    } = modelRtbs || {};
    const modelAccounts = model.accounts ?? [];

    return {
        ...model,
        ...modelRtbs,
        accountsCount: modelAccounts.length,
        accounts: combineAccountsWithRtbs(modelAccounts, accountRtbs, fxRate, baseCurrency),
        baseCurrency,
        balance: balance * fxRate,
        cashOnAccount: cashOnAccount * fxRate,
        markToMarket: markToMarket * fxRate,
        totalDividendAmount: totalDividendAmount * fxRate,
        totalDividendYield,
        sodCashBalance: sodCashBalance * fxRate,
        sodMarginBalance: sodMarginBalance * fxRate,
        sodShortBalance: sodShortBalance * fxRate,
        defaultRebalanceThreshold: defaultRebalanceThreshold,
    };
};

type FormatModelsWithRtbsAndBaseCurrencyParams = {
    models: Model[];
    memberRealtimeBalances: ModelRtbs[];
    baseCurrency: string;
    fxRate: number;
    thresholdsById: Map<string, number>;
};

export const formatModelsWithRtbsAndBaseCurrency = ({
    models,
    memberRealtimeBalances,
    baseCurrency,
    fxRate,
    thresholdsById,
}: FormatModelsWithRtbsAndBaseCurrencyParams): ModelWithBaseCurrency[] =>
    models.map((model) => {
        const modelWithRtbs = memberRealtimeBalances.find((modelRtbs) => modelRtbs.modelId === model.id);
        return formatModelWithRtbsAndBaseCurrency({
            model,
            modelRtbs: modelWithRtbs,
            baseCurrency,
            fxRate,
            defaultRebalanceThreshold: thresholdsById.get(model.id.toString()) ?? 0,
        });
    });

export const parseModelRebalancingThresholdFromJson = (settings: Record<string, string>): Map<string, number> => {
    return Object.entries(settings ?? {}).reduce((acc, [key, value]) => {
        if (!key.startsWith(MODEL_PREFERENCES_DATA_PREFIX)) {
            return acc;
        }
        const id = key.replace(`${MODEL_PREFERENCES_DATA_PREFIX}.`, '');
        try {
            const parsed = JSON.parse(value);
            const threshold = Number(parsed?.defaultRebalanceThreshold ?? parsed);

            if (!Number.isNaN(threshold)) {
                acc.set(id, threshold);
            }
        } catch (error) {
            console.error(`Invalid model data JSON for key ${key}:`, error);
        }

        return acc;
    }, new Map<string, number>());
};
